import PropTypes from "prop-types";
import {useEffect, useMemo} from "react";
import {Components, useActive} from "traitify-widgets";
import {assign} from "common/actions/location";
import {setModal} from "common/actions/modal";
import Loading from "common/components/pages/loading";
import useAppContext from "common/hooks/use-app-context";
import useDispatch from "common/hooks/use-dispatch";
import usePageClass from "common/hooks/use-page-class";
import traitify from "common/lib/traitify";
import Modal from "./modal";
import useSurveyEffect from "./use-survey-effect";
import "./style.scss";

function Survey({redirect}) {
  const active = useActive();
  const dispatch = useDispatch();
  const {completed, loading} = useSurveyEffect({redirect});

  useEffect(() => {
    if(!active) { return; }
    if(loading) { return; }

    dispatch(setModal({show: true, type: "Survey"}));
  }, [active?.id]);

  useEffect(() => {
    if(!completed) { return; }

    assign(redirect);
  }, [completed]);

  if(loading) { return <Loading />; }

  return (
    <div className="absolute bg-default left-0 m-0 max-w-full px-md py-0 right-0 top-0 z-20">
      <Modal />
      <Components.Survey.Container />
    </div>
  );
}

Survey.propTypes = {redirect: PropTypes.string.isRequired};

function SurveyContainer({
  assessmentID = null,
  benchmarkID = null,
  orderID = null,
  packageID = null,
  redirect,
  surveyType = null
}) {
  const {current: {user}, locale} = useAppContext();
  const localeCode = locale.code.toLowerCase();
  const widgetProps = useMemo(() => {
    if(!user) { return {}; }

    const traitifyProps = traitify.props;
    const _widgetProps = {
      ...traitifyProps,
      options: {
        ...traitifyProps.options,
        allowFullscreen: false,
        imagekit: true,
        survey: {disableTimeLimit: true}
      },
      profileID: user.profile_uuid
    };

    if(assessmentID) { _widgetProps.assessmentID = assessmentID; }
    if(benchmarkID) { _widgetProps.benchmarkID = benchmarkID; }
    if(orderID) { _widgetProps.orderID = orderID; }
    if(packageID) { _widgetProps.packageID = packageID; }
    if(surveyType) { _widgetProps.options.surveyType = surveyType.toLowerCase(); }

    return _widgetProps;
  }, [
    assessmentID,
    benchmarkID,
    orderID,
    packageID,
    surveyType,
    user?.profile_uuid
  ]);

  useEffect(() => { traitify.updateLocale(localeCode); }, [localeCode]);
  usePageClass("survey");

  return (
    <Components.Container {...widgetProps}>
      <Survey redirect={redirect} />
    </Components.Container>
  );
}

SurveyContainer.propTypes = {
  assessmentID: PropTypes.string,
  benchmarkID: PropTypes.string,
  orderID: PropTypes.string,
  packageID: PropTypes.string,
  redirect: PropTypes.string.isRequired,
  surveyType: PropTypes.string
};

export default SurveyContainer;
